import {
	CARD_LABEL_INSET,
	CARD_MOUNT,
	ScoreChart,
} from '@/components/CarePage/ScoreChart'
import { StatusPill } from '@/components/Rows'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { type ScoreReading, buildScoreSeries } from '@/features/care/scoreSeries'
import { Skeleton } from '@/ui/core/skeleton'
import { Text } from '@/ui/core/typography'
import type { ReactNode } from 'react'
import { View } from 'react-native'

const SHADOW = { boxShadow: '0 4px 10px rgba(120,62,58,0.10)' }

/** The title row, inside the card: the mark, the name, and whatever the state needs beside it. */
const Label = ({ trailing }: { trailing?: ReactNode }) => (
	// The two points the card gave up to the mount, taken back here: the label sits where
	// fourteen points of card padding used to put it, while the panel below it keeps its twelve.
	<View
		className="flex-row items-center justify-between"
		style={{ marginHorizontal: CARD_LABEL_INSET }}
	>
		<View className="flex-row items-center" style={{ gap: 10 }}>
			<View className="h-10 w-10 items-center justify-center rounded-full bg-blush">
				<Glyph name="sparkle" size={20} color={colors.ink} />
			</View>
			<Text size="body3" tone="soft">
				Wally Score
			</Text>
		</View>
		{trailing}
	</View>
)

/**
 * The score at the top of Care: the last four readings, and nothing else.
 *
 * It has been three things. A seventy-point numeral with a measure bar under it, which could not
 * show that a member had climbed twelve points in two years. Then the chart, with the clinician's
 * findings folded into a blush panel that opened on a tap. Now just the chart: a card that reads
 * and does not open.
 *
 * The findings left because the visits under it are where a member's data lives now — each visit
 * opens its own record, and a finding belongs to the visit it was found at rather than to the
 * newest one. The card kept a chevron for a while after the expansion went, which promised
 * something to open that was no longer there; a card with nothing to do has no chevron, the same
 * rule a visit with no results follows in the list below.
 *
 * `readings` is the member's released results, newest in any order — the chart sorts them. Until
 * the history endpoint lands there is only ever the latest one to hand, so the card draws a single
 * bar and three slots climbing from it; that is a true state rather than a placeholder, and the
 * same component draws the full series the day the rest arrive.
 */
export const ScoreCard = ({
	readings = [],
	isLoading,
}: { readings?: ScoreReading[]; isLoading: boolean }) => {
	const slots = buildScoreSeries(readings, new Date())
	// Nothing released yet. Taken from the readings themselves rather than from a score passed in
	// beside them: the card showed no figure of its own any more, so a `score` prop was a second
	// source for one fact, and the two could disagree.
	const waiting = readings.length === 0

	if (isLoading)
		return <Skeleton isLoading className="h-40 w-full rounded-note bg-blush" />

	return (
		<View className="rounded-note bg-white" style={[SHADOW, { padding: CARD_MOUNT }]}>
			<Label
				trailing={
					waiting ? (
						// The pill is centred in the 40pt label row, so it clears the card's top by
						// 24pt. This gives its right edge the same, instead of the mount's 14.
						<View style={{ marginRight: 10 }}>
							{/* Not "Ready After Next Visit", which the app cannot promise: a visit
							    only scores if it was evaluated, so the next one may leave this card
							    exactly as it is. This says where the member stands instead, and
							    stays true for someone who has been in twice and still has nothing
							    to read. */}
							<StatusPill status="wait" label="No Score Yet" />
						</View>
					) : undefined
				}
			/>
			<ScoreChart slots={slots} />
		</View>
	)
}
